import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as s from './styled-variables';
import { Icon, ModalConfirmation } from '../../components';
import CadastroVariavel from '../register-variable/register-variable';
import {
  isAbortError,
  notifyError,
  notifySuccess,
  sessionCache,
  useDebounce,
} from '../../utils';
import {
  deleteVariable,
  getCustomers,
  getMaquinaFilters,
  getVariablesFiltered,
  updateVariable,
} from '../../services';


// "Todos" como primeira opcao em todos os selects de filtro.
const defaultOption = { label: 'Todos', value: '' };

// Inline icons (lucide-style) — usados no chrome da tela. Acoes da
// linha (pencil/trash/refresh/eye) continuam usando <Icon> porque
// sao trocaveis e o componente ja resolve.
const ISearch = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const IPlus = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

// Indicador de sort (▲▼ neutro / ▲ ou ▼ solo quando coluna ordenada).
const ISortIdle = (
  <svg viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
    <path d="M6 2.5L3.5 5h5L6 2.5zM6 9.5L8.5 7h-5L6 9.5z" />
  </svg>
);
const ISortAsc = (
  <svg viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
    <path d="M6 3l4 5H2l4-5z" />
  </svg>
);
const ISortDesc = (
  <svg viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
    <path d="M6 9l4-5H2l4 5z" />
  </svg>
);

const RelacaoVariaveis = () => {

  // Variaveis vem do backend via GET /variable/filtered.
  // Adapter `adaptVariableRow` (definido abaixo) normaliza pra shape
  // do JSX existente (name/machine/type/unit/customer/status etc.).
  const [variables, setVariables] = useState([]);
  const [totalVariables, setTotalVariables] = useState(0);
  const [loading, setLoading] = useState(true);
  // Confirmacao de delete: armazena a variavel sendo deletada; null
  // significa modal fechado.
  const [variableToDelete, setVariableToDelete] = useState(null);
  // Modal lateral de visualizacao read-only. null = fechado.
  const [variableToView, setVariableToView] = useState(null);
  // Modal de cadastro/edicao de variavel (mesmo componente, modo modal).
  //   null     = fechado
  //   'create' = aberto pra criar
  //   row      = aberto pra editar
  const [variableModal, setVariableModal] = useState(null);
  // Id sincronizando agora (mostra spinner inline + desabilita botao).
  // Permite que o operador continue interagindo com outras linhas
  // enquanto a sync de uma roda.
  const [syncingId, setSyncingId] = useState(null);
  // Dropdown options vindas de endpoints proprios (com sessionCache).
  // Note: nao temos endpoint pra listar TODAS as things (so paginado),
  // entao o filtro "Maquina" foi removido — operador filtra por Estacao
  // (parque) que e mais abrangente.
  const [customerList, setCustomerList] = useState([]);
  const [parkList, setParkList] = useState([]);
  const [thingTypeList, setThingTypeList] = useState([]);
  const [filterText, setFilterText] = useState('');
  // debouncedFilterText so atualiza 350ms apos o operador parar de
  // digitar — evita re-filtrar/re-render o array a cada keystroke.
  const debouncedFilterText = useDebounce(filterText, 350);
  const [currentPage, setCurrentPage] = useState(0);
  const [itensPerPage, setItensPerPage] = useState(10);

  const [currentStation, setCurrentStation] = useState('');
  const [currentCustomer, setCurrentCustomer] = useState('');
  const [currentType, setCurrentType] = useState('');
  const [currentDevice, setCurrentDevice] = useState('');

  // Ordenacao: chave + direcao ('asc' | 'desc'). Click no header toggla
  // direcao; click em coluna diferente troca pra essa coluna com asc.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Sincronizar: re-dispara variableSync no backend via PATCH com body
  // vazio (o endpoint update ja chama o saga de sync no final). Sem
  // alteracao de dados — so reprocessa. Util quando a variavel ficou
  // "Pendente" e o operador quer forcar nova tentativa.
  const handleSync = async (row) => {
    if (syncingId) return; // ja tem uma sync rodando
    setSyncingId(row.id);
    try {
      await updateVariable(row.id, {});
      notifySuccess(`Sincronização disparada para "${row.name}".`);
      loadVariables();
    } catch (err) {
      notifyError(`Erro ao sincronizar "${row.name}".`);
    } finally {
      setSyncingId(null);
    }
  };

  // Options de dropdowns: vem das listas fetchadas via API. cache de
  // sessao em buscaFilterOptions abaixo.
  const customerOptions = useMemo(
    () => [
      defaultOption,
      ...customerList.map((c) => ({ label: c.name, value: c.id })),
    ],
    [customerList],
  );
  const stationOptions = useMemo(
    () => [
      defaultOption,
      ...parkList.map((p) => ({ label: p.name, value: p.id })),
    ],
    [parkList],
  );
  const deviceOptions = useMemo(
    () => [
      defaultOption,
      ...thingTypeList.map((tt) => ({ label: tt.type, value: tt.id })),
    ],
    [thingTypeList],
  );
  // Type filter: labels conhecidas (backend filtra por string label).
  const typeOptions = useMemo(
    () => [
      defaultOption,
      { label: 'Supervisório', value: 'Supervisório' },
      { label: 'Alarme', value: 'Alarme' },
      { label: 'Setpoint', value: 'Setpoint' },
      { label: 'Operação', value: 'Operação' },
      { label: 'Parâmetros', value: 'Parâmetros' },
    ],
    [],
  );

  // Fetch das listas de dropdown (clientes, parques, things, tipos)
  // — uma vez por sessao via sessionCache.
  const loadFilterOptions = useCallback(async () => {
    try {
      const [customersResp, filtersResp] = await Promise.all([
        sessionCache('allCustomers', 'all', () =>
          getCustomers('', '', '', '', ''),
        ),
        sessionCache('relacaoMaquinasFilters', 'all', () =>
          getMaquinaFilters(),
        ),
      ]);
      setCustomerList(
        (customersResp && customersResp.data && customersResp.data[0]) || [],
      );
      const filters = (filtersResp && filtersResp.data) || {};
      // getMaquinaFilters retorna { park: [[parks]], thingType: [[types]],
      // applianceType: [[types]] } — extraimos o array interno.
      setParkList((filters.park && filters.park[0]) || []);
      setThingTypeList((filters.thingType && filters.thingType[0]) || []);
    } catch (err) {
      // Falha silenciosa: dropdowns viram so "Todos". Notificacao no
      // fetch principal de variaveis e suficiente pra alertar o
      // operador que algo esta errado.
    }
  }, []);

  // AbortController da request em-voo. Quando o operador troca filtros
  // rapido (ex: digita na busca), as requests antigas viram lixo — se
  // a resposta antiga chegar DEPOIS da nova, sobrescreve dados corretos
  // (race condition). Cancelamos a antiga antes de disparar a nova.
  const fetchAbortRef = useRef(null);

  // Fetch principal de variaveis — server-side com paginacao + filtros.
  const loadVariables = useCallback(async () => {
    // Cancela request anterior, se ainda em-voo.
    if (fetchAbortRef.current) fetchAbortRef.current.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    setLoading(true);
    const startedAt = Date.now();
    try {
      const resp = await getVariablesFiltered({
        name: debouncedFilterText || undefined,
        type: currentType || undefined,
        customerId: currentCustomer || undefined,
        applianceId: currentStation || undefined,
        device: currentDevice || undefined,
        page: currentPage + 1,
        limit: itensPerPage,
        signal: controller.signal,
      });
      // Se a request foi cancelada por uma nova mais recente, ignora.
      if (controller.signal.aborted) return;
      const elapsed = Date.now() - startedAt;
      // Log de performance ajuda a detectar regressao no backend.
      // Threshold de 2s: avisa que a query esta passando do aceitavel.
      if (elapsed > 2000) {
        // eslint-disable-next-line no-console
        console.warn(
          `[relacao-variaveis] /variable/filtered demorou ${elapsed}ms`,
        );
      }
      const payload = (resp && resp.data) || resp || {};
      setVariables(payload.data || []);
      setTotalVariables(payload.total || 0);
    } catch (err) {
      // Cancelamento nao deve mostrar erro pro operador.
      if (isAbortError(err) || controller.signal.aborted) return;
      notifyError('Erro ao carregar variáveis');
      setVariables([]);
      setTotalVariables(0);
    } finally {
      // Evita "loading=false" ficar pendurado quando a request foi
      // cancelada (a nova ja setou loading=true e vai resolver).
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [
    debouncedFilterText,
    currentType,
    currentCustomer,
    currentStation,
    currentDevice,
    currentPage,
    itensPerPage,
  ]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  useEffect(() => {
    loadVariables();
    // Cleanup: aborta request em-voo quando o componente desmontar ou
    // antes do proximo fetch — evita setState em componente desmontado.
    return () => {
      if (fetchAbortRef.current) fetchAbortRef.current.abort();
    };
  }, [loadVariables]);

  // ESC fecha o modal de detalhe. Cleanup obrigatorio pra nao deixar
  // listener pendurado quando o componente desmontar.
  useEffect(() => {
    if (!variableToView) return undefined;
    const handleKey = (event) => {
      if (event.key === 'Escape') setVariableToView(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [variableToView]);

  // Reseta pra pagina 1 quando filtros/itensPerPage mudam.
  useEffect(() => {
    setCurrentPage(0);
  }, [
    debouncedFilterText,
    itensPerPage,
    currentStation,
    currentCustomer,
    currentType,
    currentDevice,
  ]);

  // Adapter: backend row -> shape esperada pelo JSX. Backend retorna
  // { id, ihmname, name (legivel), unit, type, range[], status }.
  // range[] tem entries por maquina com {thing, status} — usamos pra
  // contar machineCount + extrair labels de exibicao.
  const adaptVariableRow = useCallback(
    (raw) => {
      const ranges = raw.range || [];
      const firstRange = ranges[0] || {};
      const firstThing = firstRange.thing || {};
      const firstAppliance = firstThing.appliance || {};
      const firstPark = firstAppliance.park || {};
      const firstCustomer = firstPark.customer || {};
      // Status: backend manda boolean | "pending". Mapeia pra label.
      let statusLabel = 'Pendente';
      if (raw.status === true) statusLabel = 'Verificada';
      else if (raw.status === false) statusLabel = 'Falha';
      // Type: relacao em variable.type (entity VariableType com campo
      // `name`). Defensivo pra outros formatos possíveis.
      const typeLabel =
        (raw.type && (raw.type.name || raw.type.label || raw.type.type)) ||
        (typeof raw.type === 'string' ? raw.type : '') ||
        '—';
      // Distinct customers nas ranges (depois do fix backend e5e48bb
      // que adiciona park.customer ao join).
      const customerNames = Array.from(
        new Set(
          ranges
            .map(
              (r) =>
                (((r.thing || {}).appliance || {}).park || {}).customer
                  ?.name,
            )
            .filter(Boolean),
        ),
      );
      return {
        id: raw.id,
        rawName: raw.ihmname || raw.name,
        name: raw.name || raw.ihmname,
        machine: firstThing.thing || firstThing.name || '—',
        machineCount: Math.max(ranges.length - 1, 0),
        type: typeLabel,
        unit: raw.unit || firstRange.unit || '—',
        customer: customerNames[0] || firstCustomer.name || '—',
        customerCount: Math.max(customerNames.length - 1, 0),
        station: firstPark.name || '—',
        device: (firstThing.type && firstThing.type.type) || '—',
        status: statusLabel,
        raw,
      };
    },
    [],
  );

  const adaptedData = useMemo(
    () => variables.map(adaptVariableRow),
    [variables, adaptVariableRow],
  );

  // Sort client-side (so na pagina atual). Para sort global, mudar pro
  // backend via params `sortBy/sortDir` quando o endpoint suportar.
  const sortedData = useMemo(() => {
    if (!sortKey) return adaptedData;
    const copy = [...adaptedData];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [adaptedData, sortKey, sortDir]);

  const pageCount = Math.ceil(totalVariables / itensPerPage) || 0;
  // Server-side ja retorna so a pagina atual — paginatedData = sortedData direto.
  const paginatedData = sortedData;

  // Lista de paginas com elipses (1, 2, 3, …, n-2, n-1, n) — match do
  // padrao .pager do design Variaveis.html.
  const pagList = useMemo(() => {
    const list = [];
    const total = pageCount;
    const pg = currentPage + 1; // 1-indexed pra logica de display
    if (total <= 7) {
      for (let i = 1; i <= total; i += 1) list.push(i);
    } else {
      list.push(1, 2, 3);
      if (pg > 5) list.push('…');
      if (pg > 3 && pg < total - 2) list.push(pg);
      if (pg < total - 4) list.push('…');
      list.push(total - 2, total - 1, total);
    }
    return Array.from(new Set(list));
  }, [pageCount, currentPage]);

  return (
    <s.Container className="fade-in">
      {/* Page Header (.ph) — titulo + sub + contador "X de Y variáveis". */}
      <s.PageHeader>
        <s.PageHeaderText>
          <s.PageTitle>Variáveis</s.PageTitle>
          <s.PageSub>
            Gerencie variáveis, tipos, unidades e vínculos com máquinas e
            dispositivos.
          </s.PageSub>
        </s.PageHeaderText>
        <s.PageCount>
          <b>{totalVariables}</b> {totalVariables === 1 ? 'variável' : 'variáveis'}
        </s.PageCount>
      </s.PageHeader>

      {/* Filters (.filters) — grid 6-col com hairlines internos:
          busca | estacao | tipo | cliente | dispositivo | CTA */}
      <s.Filters>
        <s.Search>
          {ISearch}
          <input
            value={filterText}
            onChange={(event) => setFilterText(event.target.value)}
            placeholder="Pesquisar variáveis (nome, máquina, cliente, dispositivo)"
          />
        </s.Search>

        <s.SelCell>
          <select
            value={currentStation}
            onChange={(e) => setCurrentStation(e.target.value)}
          >
            <option value="">Estação</option>
            {stationOptions
              .filter((o) => o.value !== '')
              .map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
          </select>
        </s.SelCell>

        <s.SelCell>
          <select
            value={currentType}
            onChange={(e) => setCurrentType(e.target.value)}
          >
            <option value="">Tipo</option>
            {typeOptions
              .filter((o) => o.value !== '')
              .map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
          </select>
        </s.SelCell>

        <s.SelCell>
          <select
            value={currentCustomer}
            onChange={(e) => setCurrentCustomer(e.target.value)}
          >
            <option value="">Cliente</option>
            {customerOptions
              .filter((o) => o.value !== '')
              .map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
          </select>
        </s.SelCell>

        <s.SelCell>
          <select
            value={currentDevice}
            onChange={(e) => setCurrentDevice(e.target.value)}
          >
            <option value="">Dispositivo</option>
            {deviceOptions
              .filter((o) => o.value !== '')
              .map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
          </select>
        </s.SelCell>

        <s.Cta
          type="button"
          onClick={() => setVariableModal('create')}
        >
          <span className="ic">{IPlus}</span>
          <span className="tx">
            <span className="t">Cadastro</span>
            <span className="s">Inserir variáveis</span>
          </span>
        </s.Cta>
      </s.Filters>

      {/* Table (.table) — grid 7-col: cada Th + cada celula de Tr e
          filho direto pra ocupar a coluna correta. Tr usa display:contents. */}
      <s.TableWrap>
        <s.Table>
          {[
            { key: 'name', label: 'Nome' },
            { key: 'machine', label: 'Máquina(s)' },
            { key: 'type', label: 'Tipo' },
            { key: 'unit', label: 'Unidade de Medida' },
            { key: 'customer', label: 'Cliente(s)' },
            { key: 'status', label: 'Status' },
          ].map(({ key, label }) => (
            <s.Th key={key} $sortable onClick={() => handleSort(key)}>
              {label}{' '}
              <span className="sort">
                {sortKey === key
                  ? sortDir === 'asc'
                    ? ISortAsc
                    : ISortDesc
                  : ISortIdle}
              </span>
            </s.Th>
          ))}
          <s.Th>Ações</s.Th>

          {loading
            ? Array.from(
                { length: itensPerPage },
                (_, i) => `sk-${itensPerPage}-${i}`,
              ).map((key) => (
                <s.Tr key={key}>
                  <s.SkeletonCell $w="80%" />
                  <s.SkeletonCell $w="70%" />
                  <s.SkeletonCell $w="55%" />
                  <s.SkeletonCell $w="40%" />
                  <s.SkeletonCell $w="75%" />
                  <s.SkeletonCell $w="60%" />
                  <s.SkeletonCell $w="65%" />
                </s.Tr>
              ))
            : paginatedData.length
            ? paginatedData.map((row) => (
                <s.Tr key={row.id}>
                  <s.TcNome data-label="Nome">{row.name}</s.TcNome>

                  <s.Tc data-label="Máquinas">
                    <s.Badges>
                      <s.Primary>{row.machine}</s.Primary>
                      {row.machineCount > 0 && (
                        <s.More>+{row.machineCount}</s.More>
                      )}
                    </s.Badges>
                  </s.Tc>

                  <s.Tc data-label="Tipo">
                    <s.TipoPill>{row.type}</s.TipoPill>
                  </s.Tc>

                  <s.TcUnidade data-label="Unidade">{row.unit}</s.TcUnidade>

                  <s.Tc data-label="Clientes">
                    <s.Badges>
                      <s.Primary>{row.customer}</s.Primary>
                      {row.customerCount > 0 && (
                        <s.More>+{row.customerCount}</s.More>
                      )}
                    </s.Badges>
                  </s.Tc>

                  <s.Tc data-label="Status">
                    <s.StatusPill $status={row.status}>
                      {row.status}
                    </s.StatusPill>
                  </s.Tc>

                  <s.Tc data-label="Ações">
                    <s.RowActions>
                      <s.IconBtn
                        title="Editar"
                        onClick={() => setVariableModal(row)}
                      >
                        <Icon name="pencil" size={16} />
                      </s.IconBtn>

                      <s.IconBtn
                        className="danger"
                        title="Excluir"
                        onClick={() => setVariableToDelete(row)}
                      >
                        <Icon name="trash-2" size={16} />
                      </s.IconBtn>

                      <s.IconBtn
                        className={row.status === 'Pendente' ? 'refresh' : ''}
                        title={
                          syncingId === row.id
                            ? 'Sincronizando...'
                            : 'Revalidar'
                        }
                        disabled={syncingId === row.id}
                        $spinning={syncingId === row.id}
                        onClick={() => handleSync(row)}
                      >
                        <Icon name="refresh" size={16} />
                      </s.IconBtn>

                      <s.IconBtn
                        title="Visualizar detalhes"
                        onClick={() => setVariableToView(row)}
                      >
                        <Icon name="eye" size={16} />
                      </s.IconBtn>
                    </s.RowActions>
                  </s.Tc>
                </s.Tr>
              ))
            : (
                <s.EmptyRow>
                  Nenhuma variável encontrada com os filtros atuais.
                </s.EmptyRow>
              )}
        </s.Table>
      </s.TableWrap>

      {/* Pager (.pager) — Anterior / 1 2 3 ... 45 46 47 / Proximo + exibir N.
          So renderiza quando ha mais de uma pagina. */}
      <s.Pager>
        <button
          className="btn"
          type="button"
          onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
          disabled={currentPage === 0 || pageCount === 0}
        >
          Anterior
        </button>
        <div className="pages">
          {pagList.map((p, idx) =>
            p === '…' ? (
              // eslint-disable-next-line react/no-array-index-key
              <span key={`dots-${idx}`} className="dots">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                className={`pg ${currentPage + 1 === p ? 'on' : ''}`}
                onClick={() => setCurrentPage(p - 1)}
              >
                {p}
              </button>
            ),
          )}
        </div>
        <button
          className="btn"
          type="button"
          onClick={() =>
            setCurrentPage((p) => Math.min(pageCount - 1, p + 1))
          }
          disabled={currentPage >= pageCount - 1 || pageCount === 0}
        >
          Próximo
        </button>
        <div className="exhibit">
          exibir
          <select
            value={itensPerPage}
            onChange={(e) => setItensPerPage(Number(e.target.value))}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </s.Pager>

      {variableToDelete && (
        <ModalConfirmation
          labelTitle={`Excluir a variável "${variableToDelete.name}"? Esta ação não pode ser desfeita.`}
          labelConfirmation="EXCLUIR"
          labelClose="CANCELAR"
          onCloseConfirmation={() => setVariableToDelete(null)}
          onClose={async () => {
            const { id, name } = variableToDelete;
            setVariableToDelete(null);
            try {
              await deleteVariable(id);
              notifySuccess(`Variável "${name}" excluída.`);
              loadVariables();
            } catch (err) {
              notifyError(`Erro ao excluir variável "${name}".`);
            }
          }}
        />
      )}

      {variableToView && (
        <>
          <s.DetailBackdrop onClick={() => setVariableToView(null)} />
          <s.DetailPanel role="dialog" aria-modal="true">
            <s.DetailHeader>
              <div>
                <s.DetailTitle>{variableToView.name}</s.DetailTitle>
                <s.DetailSubtitle>Detalhes da variável</s.DetailSubtitle>
              </div>
              <s.DetailCloseButton
                onClick={() => setVariableToView(null)}
                title="Fechar (ESC)"
              >
                <Icon name="x" size={18} />
              </s.DetailCloseButton>
            </s.DetailHeader>

            <s.DetailBody>
              <s.DetailGrid>
                <s.DetailSection>
                  <s.DetailSectionLabel>Código (IHM)</s.DetailSectionLabel>
                  <s.DetailSectionValue>
                    {variableToView.rawName || '—'}
                  </s.DetailSectionValue>
                </s.DetailSection>
                <s.DetailSection>
                  <s.DetailSectionLabel>Tipo</s.DetailSectionLabel>
                  <s.TypeBadge type={variableToView.type}>
                    {variableToView.type}
                  </s.TypeBadge>
                </s.DetailSection>
                <s.DetailSection>
                  <s.DetailSectionLabel>Unidade</s.DetailSectionLabel>
                  <s.DetailSectionValue>
                    {variableToView.unit || '—'}
                  </s.DetailSectionValue>
                </s.DetailSection>
                <s.DetailSection>
                  <s.DetailSectionLabel>Status</s.DetailSectionLabel>
                  <s.StatusBadge status={variableToView.status}>
                    {variableToView.status}
                  </s.StatusBadge>
                </s.DetailSection>
              </s.DetailGrid>

              <s.DetailSection>
                <s.DetailSectionLabel>
                  Máquinas vinculadas (
                  {(variableToView.raw?.range || []).length})
                </s.DetailSectionLabel>
                <s.DetailRangeList>
                  {(variableToView.raw?.range || []).length === 0 ? (
                    <s.DetailRangeMeta>
                      Nenhuma máquina vinculada.
                    </s.DetailRangeMeta>
                  ) : (
                    (variableToView.raw?.range || []).map((r, idx) => {
                      const thing = r.thing || {};
                      const appliance = thing.appliance || {};
                      const park = appliance.park || {};
                      const customer = park.customer || {};
                      return (
                        <s.DetailRangeItem key={`range-${r.id || idx}`}>
                          <s.DetailRangePrimary>
                            {thing.thing || thing.name || '—'}
                          </s.DetailRangePrimary>
                          <s.DetailRangeMeta>
                            {[
                              appliance.name,
                              park.name,
                              customer.name,
                            ]
                              .filter(Boolean)
                              .join(' · ') || '—'}
                          </s.DetailRangeMeta>
                        </s.DetailRangeItem>
                      );
                    })
                  )}
                </s.DetailRangeList>
              </s.DetailSection>
            </s.DetailBody>
          </s.DetailPanel>
        </>
      )}

      {/* Modal de cadastro/edicao — reusa o componente CadastroVariavel
          em modo modal. Cria quando variableModal === 'create', edita
          quando e uma row. Apos salvar, recarrega a lista via loadVariables. */}
      {variableModal !== null && (
        <CadastroVariavel
          /* key forca remount quando troca entre criar/editar ou entre
             rows diferentes — garante state limpo no form sem depender
             do useEffect de sync. */
          key={variableModal === 'create' ? 'new' : `edit-${variableModal.id}`}
          asModal
          variable={variableModal === 'create' ? null : variableModal}
          onClose={() => setVariableModal(null)}
          onSaved={() => loadVariables()}
        />
      )}
    </s.Container>
  );
};

export default RelacaoVariaveis;
